/**
 * Captures the social-OAuth handoff out of the URL fragment, before anything
 * else in the app can observe it. Mirrors src/lib/agentInviteBootstrap.ts and
 * src/lib/orgInviteBootstrap.ts.
 *
 * WHAT IS BEING PROTECTED
 * Two values land on /auth/social/callback: a one-time opaque `state` and a
 * provider AUTHORIZATION CODE. The code is a short-lived bearer credential —
 * whoever exchanges it first gets the access token — and the state is the only
 * thing that can spend the pending transaction. Neither may reach a log.
 *
 * WHY A FRAGMENT AND NOT A QUERY PARAMETER
 * A `?code=` query parameter is sent to the server on the initial page request,
 * so it is recorded with Vercel's runtime request details and travels in the
 * `Referer` header to any third-party asset the page loads. A fragment is
 * processed by the browser and never leaves it. supabase/functions/
 * social-auth-callback exists purely to convert the provider's query string into
 * this fragment, on a hop the browser makes rather than the server.
 *
 * There is deliberately NO query-string fallback here. If a browser, extension
 * or intermediary strips the fragment, this captures nothing, the page fails
 * closed, and the transaction expires at its own ten-minute deadline. Reading a
 * query parameter instead would re-create the exposure the fragment exists to
 * remove. A `?code=`/`?state=` that somehow arrives IS scrubbed — it must not
 * linger in the address bar or a referrer — but it is never read.
 *
 * WHY THIS MODULE SELF-EXECUTES ON IMPORT
 * Ordering is the whole point. ES module side effects run in import order and
 * before any statement in the importing module, so importing this file in
 * src/main.tsx — after the two invitation bootstraps, and before
 * `@sentry/react`, `App`, or anything that can mount Vercel Analytics —
 * guarantees the fragment is gone before `Sentry.init()` runs and before any
 * other module reads `window.location`.
 * src/lib/socialAuthCallbackBootstrap.test.ts pins that position.
 *
 * WHERE THE HANDOFF LIVES
 * In `captured` below — module-scoped browser memory, and nowhere else. Never a
 * query parameter, localStorage, sessionStorage, a cookie, `history.state`, the
 * console, Sentry context, or analytics. A refresh therefore loses it and the
 * connect attempt fails closed. That is correct: persisting an authorization
 * code to survive F5 is exactly the thing worth not doing.
 */

/**
 * Mirrors isValidOAuthState in supabase/functions/_shared/socialOAuthState.ts,
 * which cannot be imported here — edge functions run on Deno and do not share
 * the src/ module graph (see CLAUDE.md). 32 random bytes as unpadded base64url
 * is always 43 characters. Keep the two in sync by hand.
 */
const STATE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * The authorization code is treated as an OPAQUE non-empty string with a
 * conservative ceiling. No character whitelist: the alphabet is the provider's,
 * not ours, and inventing one would reject valid codes from a provider we have
 * not seen. The finalizer applies the same bound.
 */
const MAX_CODE_LENGTH = 2048;

/** The only route this may act on. */
const ROUTE = "/auth/social/callback";

/**
 * What the page hands to social-auth-finalize. There is no organization,
 * platform or user id in it, and there is nowhere to put one: the finalizer
 * reads all three from the claimed transaction and the verified JWT.
 *
 * `denied` is one fixed marker for every "no usable authorization code" case —
 * a provider refusal, a missing code, or a malformed one. The distinction would
 * tell the caller nothing and the finalizer treats them identically: it spends
 * the state and reports a generic failure.
 */
export type SocialAuthHandoff =
  | { state: string; code: string }
  | { state: string; denied: true };

/** Module-scoped browser memory. Never persisted, never serialised. */
let captured: SocialAuthHandoff | null = null;

/** The minimum surface this needs, so it can be exercised without a DOM. */
export interface BootstrapWindow {
  location: { pathname: string; hash: string; search: string };
  history: { replaceState(state: unknown, unused: string, url: string): void };
}

export type CaptureOutcome =
  /** A usable handoff was taken into memory and the URL was cleaned. */
  | "captured"
  /** Something was present but unusable; the URL was still cleaned. */
  | "scrubbed"
  /** Nothing to do — not the route, or nothing in the URL at all. */
  | "noop";

/** Collapses trailing slashes the way the org bootstrap does: a link rewriter
 *  or a retyped URL can add them, and React Router renders every variant as the
 *  same route, so all of them must be scrubbed. */
function normalisePath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;
}

/**
 * Pure fragment parser. Returns a handoff only for the expected shape on exactly
 * this route. A valid state with an unusable code is deliberately still a
 * handoff — carrying the state forward is what lets the AUTHENTICATED finalizer
 * burn it, which an anonymous caller must never be able to do.
 */
export function parseSocialAuthFragment(
  pathname: string,
  hash: string,
): SocialAuthHandoff | null {
  if (normalisePath(pathname) !== ROUTE) return null;
  if (!hash.startsWith("#")) return null;

  const params = new URLSearchParams(hash.slice(1));
  const state = params.get("state");
  if (!state || !STATE_PATTERN.test(state)) return null;

  if (params.get("denied") === "1") return { state, denied: true };

  const code = params.get("code");
  if (code && code.length > 0 && code.length <= MAX_CODE_LENGTH) return { state, code };

  return { state, denied: true };
}

/**
 * Reads the fragment, keeps a usable handoff in memory, and rewrites the URL to
 * a bare route — dropping the fragment AND any query string. Passes `null` as
 * the history state so nothing can end up in `history.state` either.
 *
 * Safe during SSR and prerendering: with no `window`, it does nothing.
 */
export function captureSocialAuthFromFragment(
  win: BootstrapWindow | undefined = typeof window === "undefined"
    ? undefined
    : (window as unknown as BootstrapWindow),
): CaptureOutcome {
  if (!win) return "noop";
  if (normalisePath(win.location.pathname) !== ROUTE) return "noop";

  const hasResidue = win.location.hash.length > 1 || win.location.search.length > 1;
  if (!hasResidue) return "noop";

  const handoff = parseSocialAuthFragment(win.location.pathname, win.location.hash);
  if (handoff) captured = handoff;

  win.history.replaceState(null, "", ROUTE);

  return handoff ? "captured" : "scrubbed";
}

/**
 * One-time read-and-clear. The page calls this once on mount; a second call, or
 * a call after a refresh, returns null and the attempt fails closed.
 */
export function takeSocialAuthHandoff(): SocialAuthHandoff | null {
  const handoff = captured;
  captured = null;
  return handoff;
}

/** Test seam. Not used by application code. */
export function __setSocialAuthHandoffForTests(handoff: SocialAuthHandoff | null): void {
  captured = handoff;
}

// The self-execution described in the header. Must stay the last statement, and
// this module must be imported in src/main.tsx before @sentry/react and App.
captureSocialAuthFromFragment();
